import React from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
} from "@mui/material";
import {
  SportsScore as SportsIcon,
  Login as LoginIcon,
  AccountCircle as AccountIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  PersonAdd as PersonAddIcon,
  Home as HomeIcon,
  Dashboard as DashboardIcon,
  Menu as MenuIcon,
  People as PeopleIcon,
  Event as EventIcon,
  Leaderboard as LeaderboardIcon,
  Notifications as NotificationsIcon,
  ManageAccounts as ManageAccountsIcon,
  AdminPanelSettings as AdminPanelIcon,
  Euro as EuroIcon,
  Gavel as GavelIcon,
  EmojiEvents as TrophyIcon,
  Campaign as CampaignIcon,
  SwapHoriz as TransferIcon,
  Delete as DeleteIcon,
  SportsHandball as CourtIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
  Calculate as CalculateIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useOrganization } from "../contexts/OrganizationContext";
import { apiRequest } from "../config/api";
import { AdminNavigationPreferences } from "../types";
import PWAInstallPrompt from "./PWAInstallPrompt";
import OfflineIndicator from "./OfflineIndicator";
import splLogo from "../img/SPL_Logo.png";
import spcLogo from "../img/SPC.png";
import padelEagleLogo from "../img/padelEagle.png";

// SPT logo placeholder — replace with the real asset when available
const sptLogo = splLogo;

interface PublicLayoutProps {
  children: React.ReactNode;
}

function PublicLayout({ children }: PublicLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, playerUser, playerLogout } = useAuth();
  const { mode, toggleTheme } = useTheme();
  const { currentOrg } = useOrganization();
  const [adminDrawerOpen, setAdminDrawerOpen] = React.useState(false);
  const [navPreferences, setNavPreferences] = React.useState<AdminNavigationPreferences | null>(null);
  const [showSeasonHeaderButton, setShowSeasonHeaderButton] = React.useState(true);
  const [isMidSeasonOnly, setIsMidSeasonOnly] = React.useState(false);
  const [publicView, setPublicView] = React.useState<"league" | "tournament" | "spt">(
    () => {
      const saved = localStorage.getItem("publicView");
      return (saved as "league" | "tournament" | "spt") || "league";
    },
  );

  // Fetch nav preferences when admin is logged in
  React.useEffect(() => {
    if (user?.role === 'admin') {
      apiRequest('/api/admin-preferences/navigation')
        .then((res) => res.ok ? res.json() : null)
        .then((prefs) => { if (prefs) setNavPreferences(prefs); })
        .catch(() => {});
    }
  }, [user]);

  // Fetch season registration status for league orgs
  React.useEffect(() => {
    if (!currentOrg || currentOrg.competitionType === 'tournament') return;
    apiRequest('/api/season-registration/status')
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data) {
          setShowSeasonHeaderButton(data.showHeaderButton !== false);
          setIsMidSeasonOnly(!data.isOpen && !!data.midSeasonIsOpen);
        }
      })
      .catch(() => {});
  }, [currentOrg]);

  // Listen for changes in publicView from localStorage
  React.useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem("publicView");
      if (saved) setPublicView(saved as "league" | "tournament" | "spt");
    };
    window.addEventListener("storage", handleStorageChange);
    // Also check periodically for same-tab changes
    const interval = setInterval(() => {
      const saved = localStorage.getItem("publicView");
      if (saved && saved !== publicView) {
        setPublicView(saved as "league" | "tournament" | "spt");
      }
    }, 100);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, [publicView]);

  // Determine if we're on the Nations League page
  const isNationsLeague = location.pathname.endsWith("/cupOfNations");

  // Determine view
  const isSPCView = currentOrg?.competitionType === "both" && publicView === "tournament";
  const isSPTView = currentOrg?.competitionType === "both" && publicView === "spt";

  // Determine organization-specific theme
  const isPadelEagle = currentOrg?.slug === "padel-eagle";
  const hasCustomBranding = !!currentOrg?.branding?.primaryColor && !isPadelEagle && !isSPCView && !isSPTView && currentOrg?.slug !== "spl";
  const orgColor = isPadelEagle
    ? "#743444"
    : isSPCView
      ? "#2d3561"
      : isSPTView
        ? "#1b5e20"
        : hasCustomBranding
          ? currentOrg!.branding!.primaryColor!
          : "#1976d2";
  const orgColorLight = isPadelEagle
    ? "#743444"
    : isSPCView
      ? "#3d4571"
      : isSPTView
        ? "#2e7d32"
        : hasCustomBranding
          ? currentOrg!.branding!.secondaryColor || currentOrg!.branding!.primaryColor!
          : "#42a5f5";
  const orgAccent = isPadelEagle
    ? "#E49421"
    : hasCustomBranding
      ? currentOrg!.branding!.accentColor || currentOrg!.branding!.primaryColor!
      : "#e3f2fd";

  // Select logo based on organization
  const getLogo = () => {
    if (currentOrg?.branding?.logo) return currentOrg.branding.logo;
    if (isNationsLeague) return spcLogo;
    if (isSPCView) return spcLogo;
    if (isSPTView) return sptLogo;
    if (isPadelEagle) return padelEagleLogo;
    return splLogo;
  };

  const getOrgName = () => {
    if (isNationsLeague) return "🏆 SPC Stuttgart Padel Cup of Nations";
    if (isSPCView) return "⚔️ SPC Stuttgart Padel Cups";
    if (isSPTView) return "🎾 SPT Stuttgart Padel Tournaments";
    if (isPadelEagle) return "🦅 Padel Eagle";
    if (currentOrg?.name) return `🎾 ${currentOrg.name}`;
    return "🎾 SPL Stuttgart Padel League";
  };

  // Simple org name for drawer (no emojis)
  const getDrawerOrgName = () => {
    if (isPadelEagle) return "Padel Eagle";
    return currentOrg?.name || "League Admin";
  };

  // Admin menu items for drawer — mirrors Layout.tsx logic
  const isLeagueOrg =
    currentOrg?.competitionType === 'league' || currentOrg?.competitionType === 'both';
  const isTournamentOrg =
    currentOrg?.competitionType === 'tournament' || currentOrg?.competitionType === 'both';

  const allAdminMenuItems = [
    { text: "Home", icon: <HomeIcon />, path: "/" },
    { text: "Dashboard", icon: <DashboardIcon />, path: "/admin/dashboard" },
    { text: "Players", icon: <PeopleIcon />, path: "/admin/players" },
    ...(isLeagueOrg ? [{ text: "League Days", icon: <EventIcon />, path: "/admin/league-days" }] : []),
    ...(isTournamentOrg ? [{ text: "Tournaments", icon: <TrophyIcon />, path: "/admin/tournaments" }] : []),
    { text: "Rankings", icon: <LeaderboardIcon />, path: "/admin/rankings" },
    ...(isLeagueOrg ? [
      { text: "Finances", icon: <EuroIcon />, path: "/admin/finances" },
      { text: "Financial Calculator", icon: <CalculateIcon />, path: "/admin/financial-calculator" },
      { text: "Penalties & Bonuses", icon: <GavelIcon />, path: "/admin/penalties" },
      { text: "Nations League", icon: <TrophyIcon />, path: "/admin/nations-league" },
    ] : []),
    { text: "Announcements", icon: <CampaignIcon />, path: "/admin/announcements" },
    { text: "Scorekeeper Requests", icon: <NotificationsIcon />, path: "/admin/scorekeeper-requests" },
    { text: "Manage Scorekeepers", icon: <ManageAccountsIcon />, path: "/admin/manage-scorekeepers" },
    { text: "Player Accounts", icon: <PeopleIcon />, path: "/admin/player-accounts" },
    { text: "Deletion Requests", icon: <DeleteIcon />, path: "/admin/deletion-requests" },
    { text: "Tournament Registrations", icon: <PeopleIcon />, path: "/admin/tournament-registration-requests" },
    { text: "Admin Users", icon: <AdminPanelIcon />, path: "/admin/admins" },
    { text: "Seasons", icon: <CalendarIcon />, path: "/admin/seasons" },
    { text: "Court Management", icon: <CourtIcon />, path: "/admin/court-management" },
    { text: "Email Settings", icon: <EmailIcon />, path: "/admin/email-settings" },
    { text: "Season Registrations", icon: <PersonAddIcon />, path: "/admin/season-registrations" },
    { text: "Transfers", icon: <TransferIcon />, path: "/admin/transfers" },
    { text: "Navigation Settings", icon: <SettingsIcon />, path: "/admin/navigation-settings" },
  ];

  const adminMenuItems = React.useMemo(() => {
    let items = allAdminMenuItems;
    // Filter by admin permissions
    if (user && user.role === 'admin' && !user.isSuperAdmin && user.permissions) {
      items = items.filter((item) => (user.permissions as string[]).includes(item.text));
    }
    if (!navPreferences?.menuOrder?.length) return items;
    // Filter hidden items
    const visible = items.filter((item) => !navPreferences.hiddenItems.includes(item.text));
    // Apply custom order
    return [...visible].sort((a, b) => {
      const ia = navPreferences.menuOrder.indexOf(a.text);
      const ib = navPreferences.menuOrder.indexOf(b.text);
      return (ia === -1 ? 9999 : ia) - (ib === -1 ? 9999 : ib);
    });
  }, [allAdminMenuItems, navPreferences, user]);

  const adminDrawer = (
    <Box sx={{ width: 240 }}>
      <Toolbar>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img
            src={getLogo()}
            alt={getDrawerOrgName()}
            style={{
              height: isPadelEagle ? "40px" : "32px",
              width: "auto",
              borderRadius: "4px",
              border: "none",
            }}
          />
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              fontSize: "0.9rem",
              color: isPadelEagle ? "#743444" : "inherit",
              fontWeight: isPadelEagle ? "bold" : "normal",
            }}
          >
            {getDrawerOrgName()}
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {adminMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                setAdminDrawerOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", ...(isPadelEagle && {
            backgroundColor: "#FFFBF7",
            // Subtle paper-like grain (SVG noise) in the brand bordeaux
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0.45 0 0 0 0 0.2 0 0 0 0 0.27 0 0 0 0.16 0'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E\")",
            backgroundSize: "160px 160px",
          }) }}>
      {/* Offline Indicator */}
      <OfflineIndicator />

      <AppBar
        position="static"
        sx={{
          background: isPadelEagle ? "#743444" : `linear-gradient(135deg, ${orgColor} 0%, ${orgColorLight} 100%)`,
          ...(isPadelEagle && {
            "& .MuiButton-root, & .MuiButton-root .MuiTypography-root": { color: "#ffffff" },
          }),
          boxShadow: isPadelEagle
            ? "none"
            : isSPCView
              ? `0 4px 20px rgba(45, 53, 97, 0.6)`
              : isSPTView
                ? `0 4px 20px rgba(27, 94, 32, 0.5)`
                : `0 4px 20px rgba(25, 118, 210, 0.3)`,
          zIndex: 1400,
        }}
      >
        <Toolbar sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
          {/* Burger menu - only visible when logged in as admin */}
          {user?.role === "admin" && (
            <IconButton
              color="inherit"
              aria-label="open admin menu"
              edge="start"
              onClick={() => setAdminDrawerOpen(true)}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box
            onClick={() => navigate("/")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, md: 2 },
              cursor: "pointer",
            }}
          >
            <img
              src={getLogo()}
              alt={getOrgName()}
              style={{
                height: isPadelEagle ? "44px" : "36px",
                width: "auto",
                borderRadius: isPadelEagle ? "10px" : "6px",
                border: isPadelEagle ? "none" : "2px solid rgba(255,255,255,0.3)",
                ...(isPadelEagle && { background: "#ffffff", padding: "4px" }),
              }}
            />
            <Typography
              variant="h5"
              component="div"
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.4rem" },
                background: isPadelEagle
                  ? "none"
                  : "linear-gradient(45deg, #fff 30%, #e3f2fd 90%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: isPadelEagle ? "#fff" : "transparent",
              }}
            >
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "inline" } }}
              >
                {getOrgName()}
              </Box>
              <Box
                component="span"
                sx={{ display: { xs: "inline", sm: "none" } }}
              >
                {isNationsLeague
                  ? "🏆 SPC"
                  : isPadelEagle
                    ? ""
                    : isSPCView
                      ? "⚔️ SPC"
                      : isSPTView
                        ? "🎾 SPT"
                        : currentOrg?.slug
                          ? `🎾 ${currentOrg.slug.toUpperCase()}`
                          : "🎾 SPL"}
              </Box>
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          {playerUser && (
            <>
              <IconButton
                color="inherit"
                onClick={() => navigate("/")}
                sx={{ mr: 1 }}
                title="Home"
              >
                <HomeIcon />
              </IconButton>
              <IconButton
                color="inherit"
                onClick={() => navigate("/player/dashboard")}
                sx={{ mr: 1 }}
                title="Dashboard"
              >
                <DashboardIcon />
              </IconButton>
            </>
          )}
          {!isPadelEagle && (
<IconButton color="inherit" onClick={toggleTheme} sx={{ mr: 1 }}>
            {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
)}
          {user ? (
            // User is logged in - show user info and logout option
            <Box sx={{ display: "flex", gap: 1 }}>
              {user.role === "admin" && (
                <IconButton
                  color="inherit"
                  onClick={() => navigate("/admin/dashboard")}
                  sx={{
                    borderRadius: 2,
                    border: "2px solid rgba(255,255,255,0.3)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                      borderColor: "rgba(255,255,255,0.5)",
                    },
                  }}
                  title="Admin Dashboard"
                >
                  <DashboardIcon sx={{ fontSize: { xs: 20, md: 24 } }} />
                </IconButton>
              )}
              <Button
                color="inherit"
                startIcon={
                  <AccountIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
                }
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                sx={{
                  borderRadius: 3,
                  px: { xs: 2, md: 3 },
                  py: 1,
                  fontSize: { xs: "0.8rem", md: "0.875rem" },
                  border: "2px solid rgba(255,255,255,0.3)",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    borderColor: "rgba(255,255,255,0.5)",
                  },
                }}
              >
                <Typography
                  sx={{
                    display: { xs: "none", sm: "block" },
                    fontSize: "inherit",
                  }}
                >
                  {user.role === "admin" ? "Admin" : "Scorekeeper"} - Logout
                </Typography>
                <Typography
                  sx={{
                    display: { xs: "block", sm: "none" },
                    fontSize: "inherit",
                  }}
                >
                  Logout
                </Typography>
              </Button>
            </Box>
          ) : playerUser ? (
            // Player is logged in - show logout
            <Button
              color="inherit"
              startIcon={<LoginIcon sx={{ fontSize: { xs: 18, md: 20 } }} />}
              onClick={() => {
                playerLogout();
                navigate("/");
              }}
              sx={{
                borderRadius: 3,
                px: { xs: 2, md: 3 },
                py: 1,
                fontSize: { xs: "0.8rem", md: "0.875rem" },
                border: "2px solid rgba(255,255,255,0.3)",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.1)",
                  borderColor: "rgba(255,255,255,0.5)",
                },
              }}
            >
              Logout
            </Button>
          ) : (
            // User is not logged in - show login and register buttons
            <Box sx={{ display: "flex", gap: 1 }}>
              {(currentOrg?.competitionType === "tournament" || showSeasonHeaderButton) && (
              <Button
                color="inherit"
                startIcon={
                  <PersonAddIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
                }
                onClick={() => {
                  if (currentOrg?.competitionType === "tournament") {
                    navigate("/view-tournaments");
                  } else {
                    navigate("/register");
                  }
                }}
                sx={{
                  borderRadius: 3,
                  px: { xs: 2, md: 3 },
                  py: 1,
                  fontSize: { xs: "0.8rem", md: "0.875rem" },
                  border: "2px solid rgba(255,255,255,0.3)",
                  background: "rgba(255,255,255,0.15)",
                  fontWeight: "bold",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.25)",
                    borderColor: "rgba(255,255,255,0.6)",
                  },
                }}
              >
                <Typography
                  sx={{
                    display: { xs: "none", sm: "block" },
                    fontSize: "inherit",
                  }}
                >
                  {currentOrg?.competitionType === "tournament"
                    ? "Register to Upcoming Tournaments"
                    : isMidSeasonOnly
                      ? "Join Mid-Season"
                      : "Register for Next Season"}
                </Typography>
                <Typography
                  sx={{
                    display: { xs: "block", sm: "none" },
                    fontSize: "inherit",
                  }}
                >
                  {currentOrg?.competitionType === "tournament"
                    ? "Tournaments"
                    : "Register"}
                </Typography>
              </Button>
              )}
              <Button
                color="inherit"
                startIcon={<LoginIcon sx={{ fontSize: { xs: 20, md: 20 } }} />}
                onClick={() => navigate("/admin/login")}
                sx={{
                  borderRadius: 3,
                  px: { xs: 1.5, sm: 2, md: 3 },
                  py: 1,
                  fontSize: { xs: "0.8rem", md: "0.875rem" },
                  border: "2px solid rgba(255,255,255,0.3)",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    borderColor: "rgba(255,255,255,0.5)",
                  },
                }}
              >
                Login/Register
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Admin Drawer */}
      {user?.role === "admin" && (
        <Drawer
          variant="temporary"
          anchor="left"
          open={adminDrawerOpen}
          onClose={() => setAdminDrawerOpen(false)}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            zIndex: 1200, // Behind AppBar (which is 1400)
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: 240,
            },
          }}
        >
          {adminDrawer}
        </Drawer>
      )}

      <Container
        maxWidth="lg"
        sx={{
          flex: 1,
          py: { xs: 2, md: 4 },
          px: { xs: 2, sm: 3 },
          minHeight: "calc(100vh - 64px - 80px)", // Account for header and footer
          width: "100%",
          maxWidth: "1200px !important",
          mx: "auto",
        }}
      >
        {children}
      </Container>
      <Box
        component="footer"
        sx={{
          py: { xs: 2, md: 3 },
          px: { xs: 1, md: 2 },
          mt: "auto",
          backgroundColor: orgColor,
          color: "white",
          textAlign: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: "0.75rem", md: "0.875rem" },
            lineHeight: 1.3,
            mb: 1,
          }}
        >
          🏆 Padel League Management System - Eat, Sleep, Padel, Repeat! 🏆
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Typography
            component="a"
            href="/impressum"
            variant="body2"
            sx={{
              color: "white",
              textDecoration: "underline",
              fontSize: { xs: "0.7rem", md: "0.8rem" },
              "&:hover": {
                color: "rgba(255,255,255,0.8)",
              },
            }}
          >
            Impressum
          </Typography>
          <Typography
            component="a"
            href="/privacy"
            variant="body2"
            sx={{
              color: "white",
              textDecoration: "underline",
              fontSize: { xs: "0.7rem", md: "0.8rem" },
              "&:hover": {
                color: "rgba(255,255,255,0.8)",
              },
            }}
          >
            Privacy Policy
          </Typography>
        </Box>
      </Box>

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />
    </Box>
  );
}

export default PublicLayout;
